import { lenient } from '@massilia/contracts'
import { AxiosError } from 'axios'
import { z } from 'zod'

/**
 * Code posé par l'API sur toutes les expirations de session de recherche
 * (apps/server/src/api/shared/errors/search-session-errors.ts).
 */
const EXPIRED_SEARCH_SESSION_CODE = 'SEARCH_SESSION_EXPIRED'

/**
 * Repli pour les versions d'API antérieures à `errorCode`. Deux formulations
 * coexistent : « Session de recherche expirée » (400 à la création de session) et
 * « Session expirée » (410 sur une BookingSession morte). Le second motif couvre
 * le premier, mais on garde les deux pour que l'intention reste lisible.
 */
const EXPIRED_SEARCH_SESSION_TEXTS = ['session de recherche expirée', 'session expirée']

/**
 * Messages génériques que NestJS renvoie sur un 500 non maîtrisé. Ils ne doivent
 * jamais s'afficher : le serveur les remplace désormais (ClientFacingExceptionFilter),
 * on garde ce garde-fou côté client pour les versions d'API plus anciennes.
 */
const GENERIC_SERVER_MESSAGES = new Set([
  'bad gateway',
  'gateway timeout',
  'internal server error',
  'service unavailable',
])

/**
 * Un message rédigé par notre serveur est une phrase française qui dit quoi faire.
 * Tout ce qui porte l'une de ces marques vient d'ailleurs — erreur OpenAI ou
 * WordPress recopiée telle quelle, trace d'exception, réponse d'un hébergeur — et
 * ne dit rien à quelqu'un qui ne code pas.
 */
const TECHNICAL_MESSAGE_PATTERNS = [
  // Mots anglais des erreurs de prestataires. Aucun n'existe en français, et les
  // faux amis n'en sont pas : « erreur », « réponse », « invalide », « se connecter »
  // ne matchent pas. « plan », « port », « prompt » restent dehors : eux sont français.
  /\b(the|is|are|was|were|not|invalid|failed|failure|error|request|response|status|unexpected|missing|required|undefined|cannot|can't|unable|timeout|timed|exceeded|internal|gateway|server|token|key|must|should|this|that|with|from|your|please|try|again|rate|limit|reached|retry|connect|connection|network|socket|host|refused|reset|parse|fetch|null)\b/i,
  // Variable d'environnement ou constante technique (OPENAI_API_KEY, DATABASE_URL)
  /\b[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+\b/,
  // Code errno de Node recopié tel quel. Énuméré plutôt que deviné : une
  // référence de dossier commençant par un E n'est pas une panne réseau.
  /\bE(?:CONN|TIMEDOUT|NOTFOUND|NETUNREACH|HOSTUNREACH|PIPE|ACCES|NOENT|AI_AGAIN)[A-Z]*\b/,
  // Code HTTP. Nommé ou en tête de segment, jamais nu : un montant à trois
  // chiffres (« 450 € ») n'est pas un code d'erreur.
  /\bHTTP\b|\b(?:erreur|error|code|statut|status)\s*:?\s*[45]\d{2}\b|(?:^|[·:|–—]\s*)[45]\d{2}\b/i,
  // Corps JSON ou objet recopié dans le message
  /[{}]/,
  // Commande recopiée depuis un journal
  /\b(npm|pnpm|yarn|docker|sudo|curl)\b/i,
]

/** Au-delà, ce n'est plus une phrase mais un dépôt de trace. */
const MAX_CLIENT_MESSAGE_LENGTH = 300

const isClientReadable = (message: string): boolean =>
  message.length <= MAX_CLIENT_MESSAGE_LENGTH &&
  !GENERIC_SERVER_MESSAGES.has(message.toLowerCase()) &&
  !TECHNICAL_MESSAGE_PATTERNS.some((pattern) => pattern.test(message))

/**
 * La frontière entre une clause `catch` et les lecteurs d'erreur ci-dessous.
 * TypeScript ne garantit rien sur ce qu'un `throw` transporte : tout ce qui
 * franchit cette porte en ressort en `Error`, le seul type que ce module lit.
 * Le paramètre s'appelle `cause` parce que c'est là qu'atterrit la valeur
 * d'origine (`ErrorOptions.cause`) quand ce n'en était pas une.
 */
export const toError = (cause: unknown): Error => {
  if (cause instanceof Error) return cause
  const message = z.string().safeParse(cause)
  return new Error(message.success ? message.data : 'Erreur inconnue', { cause })
}

/**
 * L'appel n'a jamais atteint le serveur : personne n'a rédigé de message, et le
 * repli de l'écran (« Une erreur est survenue. ») ne dit ni ce qui s'est passé ni
 * quoi faire. On le complète — les générations d'images et d'articles dépassent
 * régulièrement le délai de 30 s du client, et c'est le cas le plus fréquent.
 */
const getTransportCause = (error: Error): string | undefined => {
  if (!(error instanceof AxiosError) || error.response) {
    return undefined
  }

  return error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT'
    ? 'Le serveur a mis trop de temps à répondre : l’opération continue peut-être de son côté, rechargez la page dans une minute.'
    : 'Le serveur n’a pas répondu : vérifiez votre connexion internet, puis réessayez.'
}

/**
 * Le corps d'erreur de l'API, décodé à la frontière réseau. Chaque champ porte
 * son propre `.catch(undefined)` : un corps partiellement inattendu (un
 * `message` non textuel renvoyé par un reverse-proxy, par exemple) ne doit pas
 * faire perdre l'`errorCode` qui, lui, est exploitable.
 */
const apiErrorResponseSchema = z
  .object({
    code: lenient(z.string()),
    errorCode: lenient(z.string()),
    message: lenient(z.string()),
  })
  .catch({})

const getResponseData = (error: Error): z.infer<typeof apiErrorResponseSchema> => {
  if (!(error instanceof AxiosError)) {
    return {}
  }
  return apiErrorResponseSchema.parse(error.response?.data)
}

export const getApiErrorMessage = (error: Error): string | undefined =>
  getResponseData(error).message

/** Code métier posé par l'API (`errorCode` dans le corps de la réponse). */
const getApiErrorCode = (error: Error): string | undefined => getResponseData(error).errorCode

/**
 * Le seul point de passage entre une erreur et un écran. Ce que NOTRE serveur a
 * rédigé (« Ce canal est déjà parti… », « L'analyse a déjà été lancée… ») ressort
 * tel quel : ces messages disent quoi faire, les écraser par un repli générique
 * perd le seul renseignement utile. Tout le reste devient une phrase française —
 * jamais un code HTTP, jamais d'anglais, jamais un nom de variable d'environnement.
 *
 * Accepte aussi un message brut (`string`) : les échecs transportés DANS une
 * réponse 200 — `run.error`, `run.lastSampleError`, l'`error` d'un canal d'envoi —
 * sont écrits par OpenAI ou WordPress et passent par la même porte. Ces champs-là
 * sont facultatifs : l'absence de message est un cas nominal, pas une anomalie,
 * et ressort simplement en repli.
 */
export const getClientFacingMessage = (
  error: Error | null | string | undefined,
  fallback: string
): string => {
  const transportCause = error instanceof Error ? getTransportCause(error) : undefined
  if (transportCause) {
    // Le repli de l'appelant nomme l'action qui a échoué : on garde ce contexte
    // et on ajoute la cause, plutôt que de choisir entre les deux.
    return `${fallback.trim().replace(/[.!?…]?$/, '.')} ${transportCause}`
  }

  const message = (
    error instanceof Error ? getApiErrorMessage(error) : (error ?? undefined)
  )?.trim()

  if (!message || !isClientReadable(message)) {
    return fallback
  }

  // Un 5xx sans code métier n'a rien de rédigé : son message décrit la panne à un
  // développeur, pas la marche à suivre à l'utilisateur.
  const status = error instanceof AxiosError ? error.response?.status : undefined
  if (
    error instanceof AxiosError &&
    status !== undefined &&
    status >= 500 &&
    !getApiErrorCode(error)
  ) {
    return fallback
  }

  return message
}

const hasStatusAndCode = (error: Error, status: number, errorCode: string): boolean =>
  error instanceof AxiosError &&
  error.response?.status === status &&
  getApiErrorCode(error) === errorCode

export const isExpiredSearchSessionError = (error: Error): boolean => {
  if (!(error instanceof AxiosError)) {
    return false
  }

  if (getApiErrorCode(error) === EXPIRED_SEARCH_SESSION_CODE) {
    return true
  }

  const status = error.response?.status
  if (status !== 400 && status !== 410) {
    return false
  }

  const message = getApiErrorMessage(error)?.toLowerCase()
  return !!message && EXPIRED_SEARCH_SESSION_TEXTS.some((text) => message.includes(text))
}

/**
 * True when the flight search failed because Affidium (our flight provider) is
 * down — the server answers HTTP 503 with errorCode 'PROVIDER_UNAVAILABLE'.
 * Lets the UI show a "maintenance" message instead of a generic error.
 */
export const isProviderUnavailableError = (error: Error): boolean =>
  hasStatusAndCode(error, 503, 'PROVIDER_UNAVAILABLE')

/**
 * Vrai quand la création du dossier chez Affidium a échoué alors qu'on sait
 * qu'aucune réservation n'a été enregistrée : le client peut relancer.
 */
export const isBookingProviderUnavailableError = (error: Error): boolean =>
  hasStatusAndCode(error, 503, 'BOOKING_PROVIDER_UNAVAILABLE')

/**
 * Vrai quand Affidium n'a pas confirmé l'issue de la réservation (silence, réponse
 * illisible) : un dossier existe peut-être. Il ne faut surtout pas relancer, mais
 * passer par l'agence.
 */
export const isBookingProviderUncertainError = (error: Error): boolean =>
  hasStatusAndCode(error, 503, 'BOOKING_PROVIDER_UNCERTAIN')

/** Vrai pour l'un ou l'autre des deux échecs de réservation ci-dessus. */
export const isBookingProviderError = (error: Error): boolean =>
  isBookingProviderUnavailableError(error) || isBookingProviderUncertainError(error)

/**
 * Discriminates the two 429 throttles emitted by the API:
 * - 'minute' → per-minute window (FlightSearchThrottle, ChatThrottle…)
 * - 'day'    → daily quota (DailySearchThrottlerGuard…)
 * Reads the structured `code` field set by the server guards; falls back to a
 * message pattern-match so older server versions still resolve correctly.
 */
export const getRateLimitScope = (error: Error): 'day' | 'minute' | null => {
  if (!(error instanceof AxiosError) || error.response?.status !== 429) {
    return null
  }
  const data = apiErrorResponseSchema.parse(error.response?.data)
  if (data.code === 'day' || data.code === 'minute') return data.code
  const message = data.message?.toLowerCase() ?? ''
  if (
    message.includes("aujourd'hui") ||
    message.includes('demain') ||
    message.includes('quotidien')
  ) {
    return 'day'
  }
  if (message) return 'minute'
  return 'minute'
}

/**
 * True quand le prestataire de paiement (SSP, FLOA) est en panne ou en
 * maintenance : le serveur répond 503 avec errorCode
 * 'PAYMENT_PROVIDER_UNAVAILABLE'. Rien à corriger côté client, seulement à
 * réessayer plus tard.
 */
export const isPaymentProviderUnavailableError = (error: Error): boolean =>
  hasStatusAndCode(error, 503, 'PAYMENT_PROVIDER_UNAVAILABLE')

/**
 * True quand le prestataire a refusé la demande pour une raison métier (ex.
 * numéro de téléphone invalide) : 400 + errorCode 'PAYMENT_PROVIDER_REJECTED'.
 * Contrairement à une panne, l'utilisateur doit corriger sa saisie.
 */
export const isPaymentProviderRejectedError = (error: Error): boolean =>
  hasStatusAndCode(error, 400, 'PAYMENT_PROVIDER_REJECTED')

export const isPaymentAlreadyInitiatedError = (error: Error): boolean =>
  hasStatusAndCode(error, 409, 'PAYMENT_ALREADY_INITIATED')
